import type { SaveUserPayload, UserAddress, UserDetail } from '@/core/models/practice.models';
import {
  EMAIL_PATTERN,
  PHONE_PATTERN,
  maxLength,
  pattern,
  required,
  requiredText,
  runValidators,
  type AddressLine,
  type FieldErrors,
  type Validator,
} from '../formRules';

// ap5-a3: matches the Cognito user-pool policy the API enforces (8+, upper, lower, digit).
export const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

// ap5-a3: what the two user types actually grant, and how the new user gets their credentials.
// APT056: this is this staff member's own sign-in permission level - there is no separate Admin/Standard
// setting for client or accountant records, and clients can be managed under either user type.
export const USER_TYPE_HINT =
  "Sets this practice staff member's own sign-in permissions - there is no separate Admin/Standard setting " +
  'for client or accountant records. Admin and Standard can both prepare, review and manage clients; only ' +
  'Admin can also approve and file accounts, and manage other users and firm settings.';
export const CONTACT_HINT = 'Optional. Link this sign-in to a contact already recorded against one of your clients.';
export const PASSWORD_HINT = 'At least 8 characters, including an upper-case letter, a lower-case letter and a number.';
export const CREDENTIALS_NOTICE =
  'No activation email is sent. The sign-in is created immediately with the password you set here, ' +
  'so you must pass the email address and password to the user yourself.';

/**
 * The reactive `userForm`. Null is what `FormGroup.reset()` leaves in a control it was not given a value for; it
 * matters for the password pair, because the match validator compares them with `!==` (null differs from '').
 */
export interface UserFormValues {
  userType: string | null;
  contact: string | null;
  title: string | null;
  firstName: string | null;
  email: string | null;
  phone: string | null;
  password: string | null;
  confirmPassword: string | null;
}

export type UserControlName = keyof UserFormValues;

export const USER_CONTROL_NAMES: UserControlName[] = [
  'userType',
  'contact',
  'title',
  'firstName',
  'email',
  'phone',
  'password',
  'confirmPassword',
];

/** `userForm.reset({ userType: '', contact: '', title: '' })`. */
export function newUserValues(): UserFormValues {
  return {
    userType: '',
    contact: '',
    title: '',
    firstName: null,
    email: null,
    phone: null,
    password: null,
    confirmPassword: null,
  };
}

/** `populate(user)`'s reset. */
export function userValuesFrom(user: UserDetail): UserFormValues {
  return {
    userType: user.userType,
    contact: user.contactId ?? '',
    title: user.title ?? '',
    firstName: user.firstName,
    email: user.email,
    phone: user.phone ?? '',
    password: '',
    confirmPassword: '',
  };
}

/** The validators each control carries; `setPasswordRequired` swaps the password pair between add and edit. */
export function validatorsFor(name: UserControlName, passwordRequired: boolean): Validator[] {
  switch (name) {
    case 'userType':
      return [required];
    case 'firstName':
      return [requiredText, maxLength(255)];
    case 'email':
      return [required, pattern(EMAIL_PATTERN), maxLength(255)];
    case 'phone':
      return [required, pattern(PHONE_PATTERN)];
    case 'password':
      // On edit a blank password keeps the current one; anything typed must meet the policy.
      return passwordRequired ? [required, pattern(PASSWORD_PATTERN)] : [pattern(PASSWORD_PATTERN)];
    case 'confirmPassword':
      return passwordRequired ? [required] : [];
    default:
      return [];
  }
}

/** A control's own validator errors (without the `passwordMismatch` the group validator may write on confirm). */
export function ownErrors(
  name: UserControlName,
  values: UserFormValues,
  passwordRequired: boolean,
): FieldErrors | null {
  return runValidators(values[name], validatorsFor(name, passwordRequired));
}

/**
 * `passwordMatchValidator` (group validator): unequal password/confirm gives the group `{ passwordMismatch: true }`.
 * Strict `!==`, so a never-typed confirm (null) differs from an emptied password ('').
 */
export function passwordsMismatch(values: UserFormValues): boolean {
  return values.password !== values.confirmPassword;
}

/**
 * The stale-error quirk: the validator writes `passwordMismatch` onto the confirm control with `setErrors`, and only
 * that control re-validating clears it. So `confirmMismatch` (the flag on the control) is recomputed when the confirm
 * changes (cleared, then set again if they differ), only ever SET when another control changes, and cleared by a
 * reset or `setPasswordRequired`.
 */
export function nextConfirmMismatch(
  previous: boolean,
  values: UserFormValues,
  changed: UserControlName | 'reset',
): boolean {
  if (changed === 'reset' || changed === 'confirmPassword') {
    return passwordsMismatch(values);
  }
  return previous || passwordsMismatch(values);
}

/** The errors of a control as Angular holds them: the mismatch flag REPLACES the control's own errors. */
export function controlErrors(
  name: UserControlName,
  values: UserFormValues,
  passwordRequired: boolean,
  confirmMismatch: boolean,
): FieldErrors | null {
  if (name === 'confirmPassword' && confirmMismatch) {
    return { passwordMismatch: true };
  }
  return ownErrors(name, values, passwordRequired);
}

/** `userForm.valid`. */
export function isUserFormValid(values: UserFormValues, passwordRequired: boolean, confirmMismatch: boolean): boolean {
  return (
    USER_CONTROL_NAMES.every((name) => controlErrors(name, values, passwordRequired, confirmMismatch) === null) &&
    !passwordsMismatch(values)
  );
}

/** `errorFor(controlName)`, only once the control is dirty or touched. Order: required, mismatch, maxlength, pattern. */
export function errorFor(
  name: UserControlName,
  values: UserFormValues,
  passwordRequired: boolean,
  confirmMismatch: boolean,
  dirtyOrTouched: boolean,
): string | null {
  const errors = controlErrors(name, values, passwordRequired, confirmMismatch);
  if (!errors || !dirtyOrTouched) {
    return null;
  }
  if (errors.required) {
    return 'This field is required.';
  }
  if (errors.passwordMismatch) {
    return 'The passwords do not match.';
  }
  if (errors.maxlength) {
    return `Maximum ${errors.maxlength.requiredLength} characters.`;
  }
  if (errors.pattern) {
    switch (name) {
      case 'email':
        return 'Enter a valid email address, e.g. name@example.co.uk.';
      case 'phone':
        return 'Enter a valid phone number (7-20 digits, spaces, + ( ) or -).';
      case 'password':
        return PASSWORD_HINT;
      default:
        return 'This value is not in the expected format.';
    }
  }
  return 'This value is not valid.';
}

export function blankAddress(): AddressLine {
  return { addressType: '0', addressLine: '', city: '', county: '', country: 'United Kingdom', postCode: '' };
}

/** The `SaveUserPayload` `save()` builds. */
export function buildUserPayload(
  id: string | null,
  value: UserFormValues,
  addresses: readonly AddressLine[],
): SaveUserPayload {
  return {
    id,
    userType: value.userType as string,
    contactId: value.contact || null,
    title: value.title || null,
    firstName: value.firstName as string,
    email: value.email as string,
    phone: value.phone || null,
    password: value.password || null,
    confirmPassword: value.confirmPassword || null,
    addresses: addresses.map<UserAddress>((row) => ({
      addressType: Number(row.addressType ?? 0),
      addressLine: row.addressLine,
      city: row.city,
      county: row.county || null,
      country: row.country || 'United Kingdom',
      postCode: row.postCode,
    })),
  };
}
